import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import QRCode from "qrcode";
import { Mono, copyText } from "@/components/uikit";

// Renders a scannable QR encoding a signer's public identity fingerprint.
// Encoded payload is PUBLIC only (never the private key).
export function IdentityQR({ signer, size = 148, testId = "identity-qr" }) {
  const [src, setSrc] = useState("");
  const [err, setErr] = useState(false);

  const payload = signer
    ? JSON.stringify({
        t: "signallink.identity",
        key_id: signer.public_key_id,
        alg: signer.algorithm,
        pub: signer.public_jwk,
      })
    : "";

  useEffect(() => {
    let active = true;
    if (!payload) return undefined;
    QRCode.toDataURL(payload, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: size * 2,
      color: { dark: "#0B1220", light: "#E6FBFF" },
    })
      .then((url) => active && setSrc(url))
      .catch(() => active && setErr(true));
    return () => {
      active = false;
    };
  }, [payload, size]);

  if (!signer) return null;

  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-cyan-900 bg-slate-950/60 p-3" data-testid={testId}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-400">Signer Fingerprint · Scan to confirm</p>
      {err ? (
        <p className="text-xs text-rose-300">Unable to render QR.</p>
      ) : src ? (
        <img src={src} alt={`Public-key fingerprint QR for signer ${signer.public_key_id}`} width={size} height={size}
          className="rounded bg-[#E6FBFF] p-1" data-testid={`${testId}-image`} />
      ) : (
        <div className="flex items-center justify-center text-slate-600" style={{ width: size, height: size }}>
          <Icons.Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <Icons.Fingerprint className="h-3.5 w-3.5 text-cyan-400" aria-hidden="true" />
        <Mono testId={`${testId}-fingerprint`}>{signer.public_key_id}</Mono>
        <button type="button" aria-label="Copy public-key fingerprint" className="text-slate-500 hover:text-cyan-400"
          onClick={() => copyText(payload, "Signer fingerprint copied")} data-testid={`${testId}-copy`}>
          <Icons.Copy className="h-3.5 w-3.5" />
        </button>
      </div>
      <p className="max-w-[220px] text-center text-[10px] leading-relaxed text-slate-500">
        Encodes the public key only ({signer.algorithm}). Not the private key. Local confirmation aid — not external identity verification.
      </p>
    </div>
  );
}
